import { createSelector } from "reselect"
import { get, chain, find, isNumber, orderBy, forEach, map } from "lodash"
import { safeDivide } from "helpers/math"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getMustAggregateFactors, getStyleExposureAggregation } from "modules/portfolio-root/selectors/portfolios"
import { getModelFactorsClassification } from "selectors/modelMetadata"
import { aggregateExposuresFactors } from "helpers/factorClassification/index"

export const getPortfolioPositionsSort = state => state.modules.litmus.sort
const getPortfolioComposition = state => get(state, "global.data.portfolioExposure.data.model.simulation.composition[0]")
const getPortfolioPositions = state => get(state, "global.data.portfolioExposure.data.model.simulation.composition[0].positions")
const getContributors = state => get(state, "global.data.portfolioExposure.data.model.simulation.exposureContributors[0].contributors")

export const getMarketValues = createSelector(
  [getPortfolioPositions, getPortfolioComposition],
  (positions, portfolioComposition) => {
    let short = 0
    let long = 0

    forEach(positions, item => {
      if (item.economicExposure > 0) {
        long += item.economicExposure
      } else {
        short -= item.economicExposure
      }
    })

    return {
      long,
      short,
      net: long - short,
      equity: get(portfolioComposition, "referenceEquity") || short + long,
    }
  },
)

export const hasReferenceEquity = createSelector(
  [getPortfolioComposition],
  (portfolioComposition) => {
    return get(portfolioComposition, "referenceEquity", null) !== null
  },
)

const getFormattedPositions = createSelector(
  [getPortfolioPositions, getMarketValues, getContributors, getMustAggregateFactors, getStyleExposureAggregation, getModelFactorsClassification],
  (positions, { equity }, contributors, mustAggregateFactors, styleAggregation, factorsClassification) => map(positions, position => {
    const contributor = find(contributors, contributor => contributor.assetClass === position.assetClass && contributor.id === position.id)
    // We use the position because we want that also to apply to unmodeled securities, which aren't returned in the contributor API
    const tickerAndMic = getTickerAndMicFromContributor(position)
    const description = get(contributor, "description")

    let exposureFactors = get(contributor, "factors")
    if (mustAggregateFactors) {
      exposureFactors = aggregateExposuresFactors(factorsClassification, exposureFactors, styleAggregation, "securityExposure")
    }

    return {
      type: "position",
      ticker: tickerAndMic.ticker,
      exchange: tickerAndMic.mic,
      key: position.id,
      description,
      assetClass: position.assetClass,
      country: get(contributor, "country"),
      currency: get(contributor, "currency"),
      classification: get(contributor, "classification.name"),
      size: position.economicExposure,
      bloombergTicker: position.bloombergTicker,
      percEquity: safeDivide(position.economicExposure, equity),
      factors: chain(exposureFactors)
        .keyBy("id")
        .mapValues(factor => factor.securityExposure)
        .value(),
    }
  }),
)

const splitLongShort = createSelector(
  [getFormattedPositions],
  (positions) => positions.reduce((accumulator, position) => {
    if (position.size < 0) {
      accumulator.short.push(position)
      accumulator.shortGmv += -position.size
    } else {
      accumulator.long.push(position)
      accumulator.longGmv += position.size
    }

    return accumulator
  }, { short: [], long: [], longGmv: 0, shortGmv: 0 }),
)

const getSortFunction = createSelector(
  [getPortfolioPositionsSort],
  ({ type, id, direction }) => {
    if (type === "factor") {
      return position => get(position, `factors.${id}`, direction === "desc" ? -Number.MAX_VALUE : Number.MAX_VALUE)
    } else {
      return position => {
        if (id === "percEquity") {
          id = "size"
        }

        const value = get(position, id, 0)
        if (isNumber(value)) {
          return Math.abs(value)
        } else {
          return value
        }
      }
    }
  },
)

export const getSortedPositions = createSelector(
  [splitLongShort, getSortFunction, getPortfolioPositionsSort],
  ({ long, short }, sortFunction, { direction }) => ({
    long: orderBy(long, sortFunction, direction),
    short: orderBy(short, sortFunction, direction),
  }),
)
